/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

// eslint-disable-next-line unicorn/prefer-node-protocol
import type { EventEmitter } from 'events';
import type { ListenerMap, TypedEventEmitter } from './typed-event-emitter';

export class ReEmitter {
  public constructor(private readonly target: EventEmitter) {}

  // Map from emitter to event name to re-emitter
  private reEmitters = new WeakMap<EventEmitter, Map<string, (...args: any[]) => void>>();

  public reEmit(source: EventEmitter, eventNames: string[]): void {
    let reEmittersByEvent = this.reEmitters.get(source);
    if (!reEmittersByEvent) {
      reEmittersByEvent = new Map();
      this.reEmitters.set(source, reEmittersByEvent);
    }

    for (const eventName of eventNames) {
      if (reEmittersByEvent.has(eventName)) continue;

      // We include the source as the last argument for event handlers which may need it,
      // such as read receipt listeners on the client class which won't have the context
      // of the room.
      const forSource = (...args: any[]): void => {
        // EventEmitter special cases 'error' to make the emit function throw if no
        // handler is attached, which sort of makes sense for making sure that something
        // handles an error, but for re-emitting, there could be a listener on the original
        // source object so the test doesn't really work. We *could* try to replicate the
        // same logic and throw if there is no listener on either the source or the target,
        // but this behaviour is fairly undesireable for us anyway: the main place we throw
        // 'error' events is for calls, where error events are usually emitted some time
        // later by a different part of the code where 'emit' throwing because the app hasn't
        // added an error handler isn't terribly helpful. (A better fix in retrospect may
        // have been to just avoid using the event name 'error', but backwards compat...)
        if (eventName === 'error' && this.target.listenerCount('error') === 0) return;
        this.target.emit(eventName, ...args, source);
      };
      source.on(eventName, forSource);
      reEmittersByEvent.set(eventName, forSource);
    }
  }

  public stopReEmitting(source: EventEmitter, eventNames: string[]): void {
    const reEmittersByEvent = this.reEmitters.get(source);
    if (!reEmittersByEvent) return; // We were never re-emitting these events in the first place

    for (const eventName of eventNames) {
      source.off(eventName, reEmittersByEvent.get(eventName)!);
      reEmittersByEvent.delete(eventName);
    }

    if (reEmittersByEvent.size === 0) this.reEmitters.delete(source);
  }
}

export class TypedReEmitter<Events extends string, Arguments extends ListenerMap<Events>> extends ReEmitter {
  public constructor(target: TypedEventEmitter<Events, Arguments>) {
    super(target);
  }

  public reEmit<ReEmittedEvents extends string, T extends Events & ReEmittedEvents>(
    source: TypedEventEmitter<ReEmittedEvents, any>,
    eventNames: T[],
  ): void {
    super.reEmit(source, eventNames);
  }

  public stopReEmitting<ReEmittedEvents extends string, T extends Events & ReEmittedEvents>(
    source: TypedEventEmitter<ReEmittedEvents, any>,
    eventNames: T[],
  ): void {
    super.stopReEmitting(source, eventNames);
  }
}
